<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import VButton from "@magnit/core/src/components/VButton/VButton.vue";
import VYaCaptcha from "@magnit/core/src/containers/VYaCaptcha/VYaCaptcha.vue";
import VInputCode from "@magnit/core/src/components/VInputCode/VInputCode.vue";
import VText from "@magnit/core/src/components/VText/VText.vue";
import type { IModalAuthCodeFormProps } from "../VModalAuth.types";
import {
  EVENT_CAPCTHA_CLICK,
  EVENT_OTP_LOGIN,
  LABEL_OTP_INCORRECT,
  LABEL_RESEND_CODE,
  LABEL_EXPIRES_IN,
} from "../constants";
import type { IYaCaptchaExpose } from "@magnit/core/src/containers/VYaCaptcha/VYaCaptcha.types";
import { waitForValue } from "@magnit/core/src/utilities/async";

const props = defineProps<IModalAuthCodeFormProps>();
const emit = defineEmits<{
  submit: [string];
  resend: [];
  analytics: [{ eventName: string; params?: Record<string, unknown> }];
}>();

const formatTimeLength = (t: number) => {
  const now = Date.now();
  let diff = t - now;
  if (diff < 1000) return [0, 0, 0, 0];
  const dd = diff > 0 ? Math.floor(diff / 1000 / 60 / 60 / 24) : 0;
  diff -= dd * 1000 * 60 * 60 * 24;
  const hh = diff > 0 ? Math.floor(diff / 1000 / 60 / 60) : 0;
  diff -= hh * 1000 * 60 * 60;
  const mm = diff > 0 ? Math.floor(diff / 1000 / 60) : 0;
  diff -= mm * 1000 * 60;
  const ss = diff > 0 ? Math.floor(diff / 1000) : 0;
  return [dd, hh, mm, ss];
};
const formatOTPExpire = (exp: number | undefined) => {
  if (!exp) return null;
  const [, , mm, ss] = formatTimeLength(exp);
  if (mm === 0 && ss === 0) return null;
  return [mm, ss].map((s) => String(s).padStart(2, "0")).join(":");
};

const code = ref<string>("code");
const captchaLoading = ref(false);
const captchaToken = defineModel<string>("captchaToken", { required: true });
const captchaRef = ref<IYaCaptchaExpose | null>(null);
const captchaLoadError = ref("");
const expireLabel = ref<null | string>(formatOTPExpire(props.expires));
const inputCodeRef = ref<null | InstanceType<typeof VInputCode>>(null);
let intervalToken: ReturnType<typeof setInterval> | null = null;

const expired = computed(() => expireLabel.value === null);
const btnLoading = computed(
  () => props.pending || props.success || captchaLoading.value,
);
const errorLabel = computed(() => props.errorText || LABEL_OTP_INCORRECT);
const submitBtnText = computed(() => {
  if (expired.value) return LABEL_RESEND_CODE;
  return `${LABEL_EXPIRES_IN} ${expireLabel.value}`;
});
const submitDisabled = computed(
  () => props.pending || !expired.value || !captchaToken.value,
);
const startCountdown = () => {
  if (expireLabel.value === null) return;
  intervalToken = setInterval(() => {
    expireLabel.value = formatOTPExpire(props.expires);
    if (expireLabel.value === null && intervalToken !== null)
      clearInterval(intervalToken);
    intervalToken = null;
  }, 1000);
};
const onYaCaptchaTokenChange = (token?: string) => {
  emit("analytics", { eventName: EVENT_CAPCTHA_CLICK });
  if (!token && captchaToken.value === token) {
    return;
  }
  captchaToken.value = token ?? "";
};
const onResend = () => {
  if (!expired.value || props.pending || props.success) return;
  emit("resend");
};

onMounted(startCountdown);
onUnmounted(() => {
  if (intervalToken) clearInterval(intervalToken);
});

watch(
  () => props.expires,
  () => {
    expireLabel.value = formatOTPExpire(props.expires);
    if (intervalToken === null) startCountdown();
  },
);
watch(
  expired,
  (next) => {
    if (next) captchaToken.value = "";
  },
  { immediate: true },
);
watch(code, async (next) => {
  if (next.length !== props.codeLength) return;
  emit("analytics", { eventName: EVENT_OTP_LOGIN });
  captchaLoading.value = true;
  if (!props.disableCaptcha) {
    try {
      captchaLoadError.value = "";
      // обработка загрузки и проверки капчи по аналогии с формой ввода телефона
      // вообще т.к. мы уже загрузили капчу шагом ранее (на форме телефона) скорее всего при вводе смс такой тщательности и не требуется
      // но для перестраховки я на всякий случай сейчас сделаю тут также.
      // пока тушим пожар, что есть репорты про бесконечный спиннер с непонятной причиной.
      // Потом надо:
      // либо попробовать вынести в отдельный композабл эту обработку капчи по клику входа.
      // либо тут достаточно оставить только вызов .execute (и waitForValue(captchaToken)) в случае невидимой капчи
      await captchaRef.value?.load();
      if (props.invisibleCaptcha) {
        captchaRef.value?.execute();
        await waitForValue(captchaToken);
      }
    } catch {
      captchaLoadError.value =
        "Ошибка загрузки капчи. Попробуйте снова или перезагрузите страницу.";
    }
  }
  if (captchaToken.value || props.disableCaptcha) {
    emit("submit", next);
  }
  captchaLoading.value = false;
});
watch(
  () => props.error,
  (next) => {
    if (!next) return;
    setTimeout(() => {
      inputCodeRef.value?.clear();
      inputCodeRef.value?.focus();
    }, 2000);
  },
);
</script>

<template>
  <section>
    <div ref="fieldRef" class="auth-otp-form__field">
      <div class="auth-otp-form__input">
        <VInputCode
          ref="inputCodeRef"
          v-model="code"
          :fields-count="codeLength"
          :has-error="error"
          :success="success"
          :disabled="pending"
        />
      </div>
      <div v-if="error" class="auth-otp-form__field--error">
        <VText font="caption-large-regular" color="system-error">
          {{ captchaLoadError || errorLabel }}
        </VText>
      </div>
    </div>
    <VButton
      full-width
      :disabled="submitDisabled"
      :loading="btnLoading"
      @click="onResend"
    >
      {{ submitBtnText }}
    </VButton>
    <VYaCaptcha
      v-if="!props.disableCaptcha && expired"
      ref="captchaRef"
      class="auth-otp-form__captcha"
      :invisible="invisibleCaptcha"
      :hide-shield="invisibleCaptcha"
      :no-auto-execute="invisibleCaptcha"
      :captcha-key="captchaKey"
      @token-changed="onYaCaptchaTokenChange"
    />
  </section>
</template>

<style lang="postcss" scoped>
.auth-otp-form {
  &__field {
    margin-bottom: var(--pl-unit-x4);
  }

  &__input {
    display: flex;
    justify-content: center;
  }

  &__captcha {
    margin: var(--pl-unit-x4) 0 0;
  }
}
</style>
